/**
 * @module AliTableAnalytics/utils/numericalConditionalFormat
 * @description 数值条件格式 — 运行时「算样式」层（第 2 层）。
 *
 * 【与第 1 层 constants 的分工】
 * - constants：图标分组、左/右规则、INumericalFormatRule 结构
 * - 本文件：读单元格原始值 → 命中哪条 rule → 输出颜色 + 左/右图标
 *
 * 【调用方】
 * - NumericalValueContent（渲染前 useMemo 调 resolveNumericalCellPresentation）
 * - 单测直接测各 export，不依赖 React
 *
 * 【不负责】
 * - 不把 conditional_format 挂到 column（见 applyColumnConditionalFormat.ts，第 3 步）
 * - 不画 DOM（见 AliTableCell 组件，第 4 步）
 */

import { Operation } from '@dbl/core/filter';
import type {
  INumericalFormatRule,
  INumericalColor,
} from '@dbl/core/chartEditor';
import { splitNumericalIconByPlacement } from '@dbl/core/chartEditor';
import { handleColorToRGBA } from '@/pages/datalab/utils/utils';

import type { ColumnDataType } from '../types';

// ---------------------------------------------------------------------------
// 一、原始值 → number（与规则阈值比较前的统一入口）
// ---------------------------------------------------------------------------

/**
 * 内部：把后端/格式化后的展示值转成可比较的 number。
 * - 支持 number、带千分位逗号的字符串、末尾带 % 的字符串
 * - 无法解析时返回 null（后续视为「无命中规则」）
 */
const toNumber = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  const text = String(raw).trim().replace(/,/g, '');
  if (!text) {
    return null;
  }
  // 展示可能是 "10.5%"，比较时去掉 %，与面板里填的阈值同一量纲
  const normalized = text.endsWith('%') ? text.slice(0, -1) : text;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * 解析单元格原始值为可比较的 number。
 *
 * @param raw - 通常为 row[column.code].value（未 format 的原始数）
 * @param valueType - 预留与列类型对齐；当前比较逻辑与 toNumber 一致
 */
export const parseCellNumericValue = (
  raw: unknown,
  valueType?: ColumnDataType,
): number | null => {
  return toNumber(raw);
};

// ---------------------------------------------------------------------------
// 二、单条 rule 是否命中（与设置面板 Operation 枚举一致）
// ---------------------------------------------------------------------------

/**
 * 用单元格数值 n 与 rule 的 operation + value[] 做比较。
 * thresholds 来自 rule.value，元素为字符串（与保存到后端的格式一致）。
 */
const compareWithThreshold = (
  n: number,
  operation: string,
  thresholds: string[],
): boolean => {
  switch (operation) {
    case Operation.Greater:
      return thresholds.length > 0 && n > Number(thresholds[0]);
    case Operation.Less:
      return thresholds.length > 0 && n < Number(thresholds[0]);
    case Operation.GreaterOrEqual:
      return thresholds.length > 0 && n >= Number(thresholds[0]);
    case Operation.LessOrEqual:
      return thresholds.length > 0 && n <= Number(thresholds[0]);
    case Operation.Equal:
      return thresholds.length > 0 && n === Number(thresholds[0]);
    case Operation.NotEqual:
      return thresholds.length > 0 && n !== Number(thresholds[0]);
    case Operation.Between: {
      if (thresholds.length < 2) {
        return false;
      }
      const low = Number(thresholds[0]);
      const high = Number(thresholds[1]);
      // 左闭右闭 [min, max]；允许用户填反序区间，取 min/max 包络
      const min = Math.min(low, high);
      const max = Math.max(low, high);
      return n >= min && n <= max;
    }
    default:
      return false;
  }
};

/** 对外：判断一条 INumericalFormatRule 是否适用于当前格数值 */
export const evaluateNumericalRule = (
  cellValue: number,
  rule: INumericalFormatRule,
): boolean => {
  const thresholds = (rule.value || []).map(String);
  return compareWithThreshold(cellValue, rule.operation, thresholds);
};

/**
 * 在列的 rules 数组里按**书写顺序**找第一条命中项。
 * 与产品约定一致：上面规则优先（面板表格从上到下）。
 */
export const resolveMatchedNumericalRule = (
  rules: INumericalFormatRule[] | undefined,
  cellValue: number | null,
): INumericalFormatRule | undefined => {
  if (cellValue === null || !rules?.length) {
    return undefined;
  }
  return rules.find(rule => evaluateNumericalRule(cellValue, rule));
};

// ---------------------------------------------------------------------------
// 三、颜色 → CSS
// ---------------------------------------------------------------------------

/** 将 { hexa, alpha } 转为单元格 style.color 可用的 rgba 字符串 */
export const numericalColorToCss = (
  color?: INumericalColor,
  fallback = 'inherit',
): string => {
  if (!color?.hexa) {
    return fallback;
  }
  return handleColorToRGBA(color) || fallback;
};

// ---------------------------------------------------------------------------
// 四、汇总为单元格展示对象（组件层唯一依赖的「算样式」API）
// ---------------------------------------------------------------------------

/** NumericalValueContent 消费的结构：文字色 + 可选左/右图标 */
export type TNumericalCellPresentation = {
  textColor: string;
  leftIcon?: string;
  rightIcon?: string;
};

/**
 * 【核心 API】根据列 rules + 当前格 rawValue 算出展示样式。
 *
 * 流程：
 *   rawValue → parseCellNumericValue
 *           → resolveMatchedNumericalRule（无命中则 inherit、无图标）
 *           → matched.color / matched.icon
 *           → splitNumericalIconByPlacement（第 1 步 constants）
 *
 * 注意：text 文案本身由表格列 format 逻辑生成，本函数只管颜色与图标。
 */
export const resolveNumericalCellPresentation = (
  rules: INumericalFormatRule[] | undefined,
  rawValue: unknown,
  valueType?: ColumnDataType,
): TNumericalCellPresentation => {
  const numeric = parseCellNumericValue(rawValue, valueType);
  const matched = resolveMatchedNumericalRule(rules, numeric);
  if (!matched) {
    return {
      textColor: 'inherit',
    };
  }

  const placement = splitNumericalIconByPlacement(matched.icon);
  return {
    textColor: numericalColorToCss(matched.color),
    leftIcon: placement.left,
    rightIcon: placement.right,
  };
};

